import { z } from 'zod'
import {
  type DirecaoMensagem,
  type MotivoFechamento,
  type OrigemChamado,
  type StatusChamado,
  type TipoMensagem,
} from '../enums'
import type { LeadAnuncioResumoDto } from './anuncios'
import type { JanelaDto, Provedor } from './api-oficial'

/**
 * Bate-papo (5.1). A parte que resolve a dor principal do projeto.
 *
 * A lista não ordena por mensagem mais recente. Ordena por estado:
 * quem está esperando resposta fica no topo e não afunda quando outra
 * conversa recebe mensagem.
 */

export const ESTADO_CONVERSA = ['aguardando', 'retorno', 'normal'] as const
export type EstadoConversa = (typeof ESTADO_CONVERSA)[number]

export interface ConversaDto {
  chamadoId: string
  /** Para o cartão poder mostrar de qual chamado se trata numa busca. */
  protocolo: string | null
  contatoId: string
  conexaoId: string
  nome: string
  /** Como o WhatsApp informou, para o "reverter" da renomeação. */
  nomeOriginal: string | null
  /** Já mascarado pelo servidor quando for o caso (4.8). */
  telefone: string | null
  telefoneMascarado: boolean

  estado: EstadoConversa
  status: StatusChamado
  naoLida: boolean
  aguardandoResposta: boolean
  aguardandoDesde: string | null
  retornoAgendadoPara: string | null
  /** Primeira vez que este cliente escreveu. Null: nunca escreveu ainda. */
  primeiraMensagemEm: string | null
  /** Escreveu pela primeira vez há menos de 72h. Decidido no servidor. */
  clienteNovo: boolean
  ultimaMensagemEm: string | null
  abertoEm: string

  previa: string | null
  previaTipo: TipoMensagem | null
  previaDirecao: DirecaoMensagem | null
  /**
   * Quem escreveu a última mensagem que saiu (mapa de uso, 08/10/2026): a
   * lista dizia "você:" em toda mensagem da empresa, e o vendedor lia "você"
   * no que o Mike escreveu. Nulo quando saiu sem autor no CRM (pelo celular,
   * pelo robô).
   */
  previaAutorId: string | null
  previaAutorNome: string | null

  /**
   * A mensagem que atendeu a "Buscar em mensagens", quando a lista veio de uma
   * busca. É o que deixa a lista abrir a conversa já no ponto certo, em vez de
   * entregar a conversa inteira e mandar a pessoa rolar procurando.
   *
   * Null nas listas comuns.
   */
  encontrada: MensagemEncontrada | null

  responsavelId: string | null
  responsavelNome: string | null
  setorNome: string | null
  /**
   * Lugar na fila (aba Fila): 1 é quem chegou primeiro (ADR 0082).
   * Null nas outras abas.
   */
  posicaoFila: number | null
  /** Apoios aceitos e ativos (ADR 0043). */
  apoios: { usuarioId: string; nome: string }[]
  /** Pedidos de apoio ainda sem resposta: o dono vê "apoio pedido a Fulano". */
  pedidosDeApoio: { usuarioId: string; nome: string }[]
  /** Pedido de apoio feito a quem está olhando, ainda sem resposta: bloco "Solicitação de apoio". */
  pedidoDeApoioParaMim: PedidoDeApoioResumo | null
  /** O suporte devolveu a conversa: bloco "Saiu do apoio" do dono até ele abrir ou responder. */
  saiuDoApoio: SaidaDoApoio | null
  temAgendamento: boolean

  /** Cronômetro de primeira resposta (4.5). Segundos desde a espera começar. */
  esperandoSegundos: number | null
  cronometroEstourado: boolean

  /**
   * Se o contato está num funil.
   *
   * Todo número que escreve já vira registro de contato, então "estar no CRM"
   * não pode significar "existe na tabela" — significa estar acompanhado num
   * funil, com etapa. É a mesma coisa que aparecer no quadro de leads.
   */
  noCrm: boolean
  funilId: string | null
  funilNome: string | null
  etapaId: string | null
  etapaNome: string | null

  /** Etiquetas do contato (4.6), para marcar sem sair da conversa. */
  tags: { id: string; nome: string; cor: string }[]
}

export interface MensagemDto {
  id: string
  chamadoId: string | null
  direcao: DirecaoMensagem
  tipo: TipoMensagem
  conteudo: string | null
  midiaUrl: string | null
  midiaNome: string | null
  midiaTamanho: number | null
  midiaMime: string | null
  /**
   * `baixando`: o arquivo ainda está na fila e a URL do WhatsApp continua de pé.
   * `indisponivel`: passou do prazo do WhatsApp e o arquivo não existe mais lá
   * — é o caso do histórico importado, e mostrar "baixando" ali seria mentira
   * eterna, com a pessoa esperando um arquivo que nunca chega.
   */
  midiaSituacao: 'ok' | 'baixando' | 'indisponivel' | 'sem-midia'
  autorId: string | null
  autorNome: string | null
  enviadaEm: string
  lidaEm: string | null
  metadados: Record<string, unknown> | null
  /**
   * A mensagem existe no WhatsApp: tem identificador e não falhou no envio.
   * É o que permite citá-la numa resposta, editar e apagar.
   */
  noWhatsapp: boolean
  /** A mensagem que esta responde, do jeito que o WhatsApp mostra em cima dela. */
  respondendoA: MensagemCitadaDto | null
  /**
   * Apagada para todos. O texto original continua em `conteudo`: o cliente
   * deixa de ver, mas o registro do atendimento não perde o que foi dito.
   */
  apagadaEm: string | null
  /** Editada: quando, e o que estava escrito antes, da versão mais antiga à mais nova. */
  editada: { em: string; anteriores: { conteudo: string; ate: string }[] } | null
}

export interface MensagemCitadaDto {
  /** A mensagem citada no CRM, quando ela está aqui; serve para pular até ela. */
  mensagemId: string | null
  /** De quem era a citada. `null` quando ela não está no CRM e não dá para saber. */
  daEmpresa: boolean | null
  trecho: string
}

/** A mensagem achada pela busca, com o pedaço do texto em volta da palavra. */
export interface MensagemEncontrada {
  mensagemId: string
  enviadaEm: string
  /** O trecho, já recortado pelo servidor, com a palavra no meio. */
  trecho: string
  /** Quantas vezes a palavra foi dita nesta conversa. */
  vezes: number
}

/** Uma das vezes em que a palavra aparece na conversa, da mais antiga à mais nova. */
export interface OcorrenciaDto {
  mensagemId: string
  enviadaEm: string
}

/**
 * O recorte de mensagens que a conversa pede.
 *
 * Vinha como texto solto direto da query: `antes` sem data válida virava
 * `Invalid Date` no meio da consulta, e `aoRedorDe` sem uuid derrubava o
 * Prisma com erro de tipo. Os dois respondiam 500 para um pedido malformado.
 */
export const filtroMensagensSchema = z.object({
  antes: z.coerce.date().optional(),
  limite: z.coerce.number().int().min(1).max(200).default(50),
  aoRedorDe: z.string().uuid().optional(),
})

export const buscaNaConversaSchema = z.object({
  termo: z.string().trim().min(3, 'a palavra precisa ter ao menos 3 letras').max(80),
})

export const filtroConversasSchema = z.object({
  conexaoId: z.string().uuid(),
  /**
   * "todas" não filtra por status: mostra aberto e encerrado na mesma lista.
   * Serve para procurar alguém sem saber se o chamado dele ainda está de pé —
   * antes era preciso olhar as duas listas e comparar na mão.
   */
  status: z.enum(['aberto', 'fechado', 'todas']).default('aberto'),
  busca: z.string().trim().max(120).optional(),
  /**
   * Protocolo exato, ou o começo dele.
   *
   * Campo separado da busca por nome de propósito: quem digita um protocolo
   * quer aquele chamado, e misturar com a busca textual traria conversa de
   * cliente cujo telefone por acaso contém a mesma sequência.
   */
  protocolo: z.string().trim().max(20).optional(),
  /**
   * Palavra dita em alguma mensagem da conversa, incluindo nota interna.
   *
   * Responde "onde falamos disso" quando ninguém lembra o nome do cliente.
   * Procura a palavra inteira, como foi escrita: acento conta, e "gengiva" não
   * traz "gengival". Mensagem escrita pelo sistema fica de fora.
   */
  mensagem: z.string().trim().max(80).optional(),
  /** "minhas" respeita responsável e apoio; "todas" mostra a fila inteira. */
  escopo: z.enum(['todas', 'minhas', 'fila']).default('todas'),

  /** A fila de uma pessoa específica, sem precisar entrar como ela. */
  responsavelId: z.string().uuid().optional(),
  tagId: z.string().uuid().optional(),
  funilId: z.string().uuid().optional(),
  etapaId: z.string().uuid().optional(),

  /**
   * Recorte por data. Em "abertos" filtra a **abertura** do chamado; em
   * "encerrados", o **encerramento** — que é a data que interessa quando se
   * olha o que já saiu da fila.
   */
  de: z.coerce.date().optional(),
  ate: z.coerce.date().optional(),

  limite: z.coerce.number().int().min(1).max(200).default(100),
})
export type FiltroConversas = z.infer<typeof filtroConversasSchema>

export const enviarMensagemSchema = z.object({
  conteudo: z.string().trim().min(1, 'escreva alguma coisa').max(4096),
  /** Nota interna não vai para o cliente (5.1). */
  notaInterna: z.boolean().default(false),
  /** A mensagem que esta responde, citada em cima dela no WhatsApp do cliente. */
  respondendoA: z.string().uuid().optional(),
})

/** O texto novo de uma mensagem editada, sem a assinatura: ela é posta de novo. */
export const editarMensagemSchema = z.object({
  conteudo: z.string().trim().min(1, 'escreva alguma coisa').max(4096),
})

/** O endereço de uma mensagem: a conversa e a mensagem dentro dela. */
export const mensagemDoChamadoSchema = z.object({
  id: z.string().uuid('id inválido'),
  mensagemId: z.string().uuid('id inválido'),
})

/** Encerramento manual, com as duas opções do pop-up (4.2). */
export const encerrarChamadoSchema = z.object({
  manterNaCarteira: z.boolean(),
})

/**
 * Transferência (4.6). O comentário é obrigatório e aceita texto; a imagem
 * colada entra na Fase 4, junto com o upload de mídia do composer.
 */
export const transferirChamadoSchema = z.object({
  setorId: z.string().uuid().optional().nullable(),
  responsavelId: z.string().uuid().optional().nullable(),
  /** Opcional desde 08/10/2026 (questionário do mapa de uso): antes era obrigatório. */
  comentario: z.string().trim().max(2000).optional().nullable(),
})
export type TransferirChamadoEntrada = z.infer<typeof transferirChamadoSchema>

export const adicionarApoioSchema = z.object({
  usuarioId: z.string().uuid(),
  comentario: z.string().trim().max(2000).optional(),
})
export type AdicionarApoioEntrada = z.infer<typeof adicionarApoioSchema>

// ─────────────────────────── apoio com pedido (ADR 0043) ───────────────────────────

export interface PedidoDeApoioResumo {
  apoioId: string
  deNome: string
  comentario: string | null
}

export interface SaidaDoApoio {
  em: string
  porNome: string | null
  recado: string | null
}

/** O pedido que abre o pop-up de quem foi chamado. */
export interface PedidoDeApoioDto {
  apoioId: string
  chamadoId: string
  clienteNome: string
  deNome: string
  comentario: string | null
  pedidoEm: string
}

/** A resposta, no pop-up rápido de quem pediu e de quem respondeu. */
export interface RespostaDeApoioDto {
  chamadoId: string
  clienteNome: string
  quemRespondeuNome: string
  aceitou: boolean
  comentario: string | null
  /** Para quem pediu, a frase fala de quem respondeu; para quem respondeu, confirma a escolha. */
  paraQuemPediu: boolean
}

export const responderApoioSchema = z.object({
  aceitar: z.boolean(),
  comentario: z.string().trim().max(2000).optional(),
})

export const sairDoApoioSchema = z.object({
  recado: z.string().trim().max(2000).optional(),
})

export const marcarLeituraSchema = z.object({
  naoLida: z.boolean(),
})

export const agendarRetornoSchema = z.object({
  /** null cancela o retorno agendado. */
  quando: z.coerce.date().nullable(),
})

/**
 * Abrir conversa: por contato existente ou por número digitado.
 *
 * O número livre é o caso de todo dia — o cliente passa o contato por
 * telefone, por indicação, num cartão. Exigir cadastro antes obrigava a sair
 * do bate-papo, ir em Leads, criar, voltar e procurar.
 */
export const abrirChamadoSchema = z
  .object({
    conexaoId: z.string().uuid(),
    contatoId: z.string().uuid().optional(),
    /** Com DDD. Cria o contato quando o número ainda não está na base. */
    telefone: z.string().trim().max(30).optional(),
    /** Nome do contato novo. Sem ele, o próprio número vira o nome. */
    nome: z.string().trim().max(160).optional(),
  })
  .refine((d) => Boolean(d.contatoId || d.telefone), {
    message: 'informe o contato ou o número',
    path: ['telefone'],
  })

export interface ChamadoDetalheDto {
  id: string
  /** Número que o cliente cita e a equipe busca: AAAAMM + 5 dígitos. */
  protocolo: string | null
  contatoId: string
  conexaoId: string
  /** O número de WhatsApp por onde a conversa entrou. */
  conexaoNome: string
  conexaoNumero: string
  status: StatusChamado
  origem: OrigemChamado
  abertoEm: string
  fechadoEm: string | null
  motivoFechamento: MotivoFechamento | null
  responsavelId: string | null
  responsavelNome: string | null
  setorId: string | null
  setorNome: string | null
  apoios: { id: string; usuarioId: string; nome: string; comentario: string | null }[]
  primeiraRespostaEm: string | null
  esperandoSegundos: number | null
  cronometroEstourado: boolean
  somenteLeitura: boolean
  /** Preenchido quando a conversa começou por um anúncio. */
  anuncio: LeadAnuncioResumoDto | null
  /** Por onde o número desta conversa fala com o WhatsApp (ADR 0033). */
  provedor: Provedor
  /** A janela de 24 horas. Só existe na API oficial; na Evolution é `null`. */
  janela: JanelaDto | null
  /** Pedidos de apoio sem resposta (ADR 0043). */
  pedidosDeApoio: { id: string; usuarioId: string; nome: string; comentario: string | null }[]
  pedidoDeApoioParaMim: PedidoDeApoioResumo | null
  /** O comentário que o pedido de apoio deixou na conversa: o ícone de apoio leva até ele. */
  mensagemDoPedidoId: string | null
  saiuDoApoio: SaidaDoApoio | null
  // [INÍCIO] Resumo Gemini da conversa (matrícula sim/nao/indefinido)
  resumoIa: string | null
  matriculaIa: 'sim' | 'nao' | 'indefinido' | null
  // [FIM]
}

/**
 * Quantas conversas há em cada aba da lista.
 *
 * "Fila" é o número que muda sozinho — conversa nova de contato sem dono cai
 * ali, e sem contador ninguém percebe até abrir a aba.
 */
export interface ContagemPorEscopo {
  todas: number
  minhas: number
  fila: number
  /** Não lidas dentro do recorte atual, para o total do topo da lista. */
  naoLidas: number
}
